import {
  Button,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import React from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router";
import i18next from "i18next";
import RegistrationLoader from "../components/common/RegistrationLoader";
import { verifySiren } from "../services/RegistrationService";
import { LANGS } from "../i18n/i18n";
import { useStyles } from "../style/sirenVerificationStyle";

/**
 * Écran de vérification préalable au formulaire d'inscription : le premier utilisateur d'un
 * compte fournisseur vide (futur admin) doit confirmer le SIREN de l'entreprise avant de pouvoir
 * remplir le formulaire d'inscription. Voir RegistrationService.verifySiren côté backend.
 *
 * @param onVerified callback appelé avec le SIREN saisi une fois la vérification réussie
 * @param registrationTheme thème courant (le SIREN ne concerne que le thème "portal")
 * @param clientName nom de l'organisation à l'origine de l'invitation (bannière)
 * @param establishmentName nom de l'établissement concerné par la demande
 */
function SirenVerificationBlock({ onVerified, registrationTheme, clientName, establishmentName }) {
  const classes = useStyles();
  const [error, setError] = React.useState(null);
  const [loader, setLoader] = React.useState(false);
  const [lang, setLang] = React.useState(LANGS.includes(i18next.language) ? i18next.language : "fr");
  const { register, errors, handleSubmit } = useForm({ mode: "onChange", delayError: 500 });
  let { registrationKey } = useParams();

  const handleLangChange = (e) => {
    setLang(e.target.value);
    localStorage.setItem("i18nextLng", e.target.value);
    window.location.href = window.location.pathname + "?lng=" + e.target.value + "&app=" + registrationTheme;
  };

  const onSubmit = async (data) => {
    // Le pattern de saisie accepte le SIREN groupé ("123 456 789") mais le backend attend
    // 9 chiffres bruts : on retire tous les espaces, pas seulement ceux en début/fin.
    const siren = (data.siren || "").replace(/\s/g, "");
    setLoader(true);
    setError(null);
    try {
      const resp = await verifySiren(registrationKey, siren);
      // Le backend répond toujours en HTTP 200 avec le statut applicatif dans le corps
      // (httpStatus "OK" | "BAD_REQUEST" | "NOT_FOUND") : un SIREN non conforme arrive donc
      // ici avec un httpStatus != "OK", et non via une exception.
      if (resp.httpStatus === "OK") {
        onVerified(siren);
      } else {
        setError(i18next.t("errors.ERROR_SIREN_MISMATCH"));
      }
    } catch (e) {
      // eaFetch ne lève que sur un statut HTTP non-2xx : erreur purement technique.
      // Le rejet fonctionnel (SIREN erroné) arrive en HTTP 200 et est géré dans le try ci-dessus.
      setError(i18next.t("errors.ERROR_UNKNOWN"));
    } finally {
      setLoader(false);
    }
  };

  return (
    <div className={classes.root}>
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        className="registrationHeader"
      >
        <Typography variant="h1" classes={{ root: "headerLabel" }}>
          {i18next.t("sirenVerification.title")}
        </Typography>

        <Select
          value={lang}
          onChange={handleLangChange}
          IconComponent={KeyboardArrowDownIcon}
          className="langSelect"
          renderValue={(value) => (
            <div className="langSelectedRoot">
              <img alt="Language" src="/assets/language.svg" />
              <span className="langSelected">{value.toUpperCase()}</span>
            </div>
          )}
        >
          {LANGS.map((langOpt) => (
            <MenuItem
              key={langOpt}
              classes={{ root: classes.langItem }}
              className={langOpt === lang ? "hidden" : ""}
              value={langOpt}
            >
              {langOpt.toUpperCase()}
            </MenuItem>
          ))}
        </Select>
      </Grid>

      <Typography
        className="invitationBanner"
        dangerouslySetInnerHTML={{
          __html: i18next.t("sirenVerification.invitation", { clientName }),
        }}
      />

      <Typography
        className="establishmentHelp"
        dangerouslySetInnerHTML={{
          __html: i18next.t("sirenVerification.help", { establishmentName }),
        }}
      />

      <div className="sectionDivider" />

      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid
          className="formContainer"
          justifyContent="center"
          alignItems="center"
          container
          spacing={0}
        >
          <Grid item xs={12}>
            <Typography className="sirenLabel">
              {i18next.t("sirenVerification.label")}
            </Typography>
            <TextField
              error={!!errors.siren}
              name="siren"
              className="sirenField"
              placeholder={i18next.t("sirenVerification.placeholder")}
              onChange={() => {
                if (error) {
                  setError(null);
                }
              }}
              inputRef={register({
                required: "errors.siren.required",
                validate: (value) =>
                  /^\d{9}$/.test((value || "").replace(/\s/g, "")) ||
                  "errors.siren.pattern",
              })}
              variant="outlined"
              helperText={errors.siren ? i18next.t(errors.siren.message) : ""}
            />
          </Grid>

          <Grid
            item
            xs={12}
            container
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            className="formBtnGrp"
          >
            <Button
              type="submit"
              variant="contained"
              size="medium"
              color="primary"
              className="greenBtn"
            >
              {i18next.t("sirenVerification.submit")}
            </Button>
          </Grid>
        </Grid>

        {loader && (
          <>
            <br />
            <RegistrationLoader text={i18next.t("loadingText")} size={50} />
          </>
        )}
        {error && <div className="error">{error}</div>}
      </form>
    </div>
  );
}

export default SirenVerificationBlock;
